import { useState, useCallback, useEffect, useRef } from 'react';
import { Key, AlertCircle } from 'lucide-react';
import { GenericListView } from '../common';
import ConfirmDialog from '../common/ConfirmDialog';
import { useToast } from '../common/Toast';
import { describeApiError } from '../../utils/apiErrors';
import { usePatient } from '../../context/patientContext';
import { useHasAnyPermission } from '../../hooks/usePermission';
import { AuthorizationsService, Authorization, PAStatus } from '../../services/patientmr/authorizations.service';
import { useAuthorizationData } from './authorizations/hooks/useAuthorizationData';
import { useAuthorizationForm } from './authorizations/hooks/useAuthorizationForm';
import { useAuthorizationPOC } from './authorizations/hooks/useAuthorizationPOC';
import { getAuthorizationColumns, getAuthorizationActions, getAuthorizationMobileCard } from './authorizations/AuthorizationList';
import { AuthorizationForm } from './authorizations/AuthorizationForm';
import { AuthorizationDetail } from './authorizations/AuthorizationDetail';
import { PADetailMode } from './authorizations/types';

const DASHBOARD_REFRESH_STATUSES = new Set([PAStatus.APPROVED, PAStatus.DENIED, PAStatus.SUBMITTED]);

interface AuthorizationsProps {
  expanded: boolean;
  toggleExpanded: () => void;
  patientId?: string;
  /** Authorization to open once the list loads (dashboard alert deep link, R-209) */
  focusId?: string;
  /** Called once focusId has been handled (opened or not found) */
  onFocusHandled?: () => void;
}

const Authorizations = ({ expanded, toggleExpanded, patientId: propPatientId, focusId, onFocusHandled }: AuthorizationsProps) => {
  const { selectedPatient: contextPatient, refreshDashboard } = usePatient();
  const patientId = propPatientId || contextPatient?.id;

  // Permission checks for authorizations
  const canCreateAuthorization = useHasAnyPermission([
    'patientmr:phi:authorization:create' as any,
    'patientmr:phi:authorization:createall' as any
  ]);

  const canReadAuthorization = useHasAnyPermission([
    'patientmr:phi:authorization:read' as any,
    'patientmr:phi:authorization:readall' as any
  ]);

  const canUpdateAuthorization = useHasAnyPermission([
    'patientmr:phi:authorization:update' as any,
    'patientmr:phi:authorization:updateall' as any
  ]);

  const canDeleteAuthorization = useHasAnyPermission([
    'patientmr:phi:authorization:delete' as any,
    'patientmr:phi:authorization:deleteall' as any
  ]);

  // Read-only mode: has read but cannot update
  const isReadOnlyView = canReadAuthorization && !canUpdateAuthorization;

  // PA detail view state
  const [paDetailMode, setPaDetailMode] = useState<PADetailMode | null>(null);
  // Authorization waiting for delete confirmation (DRAFT only, soft delete)
  const [deleteTargetId, setDeleteTargetId] = useState<string | number | null>(null);
  const [deleting, setDeleting] = useState<boolean>(false);
  const { showToast } = useToast();
  const [selectedAuth, setSelectedAuth] = useState<Authorization | null>(null);

  // Use hooks
  const {
    authorizations,
    loading,
    error,
    hasLoadedOnce,
    refreshing,
    refreshAuthorizations,
    setError
  } = useAuthorizationData({ patientId, expanded });

  const { pocCertifications } = useAuthorizationPOC({ patientId, expanded });

  const form = useAuthorizationForm({
    patientId,
    pocCertifications,
    onSuccess: () => refreshAuthorizations(),
    onError: (err) => setError(err)
  });

  // PA detail handlers
  const openPADetail = useCallback(async (auth: Authorization, mode: PADetailMode) => {
    setSelectedAuth(auth);
    setPaDetailMode(mode);

    if (!patientId) return;

    try {
      // Hydrate detail with latest server state so linked form associations are not lost to stale list data.
      const authId = auth.authorizationId || String(auth.id);
      const freshAuth = await AuthorizationsService.getAuthorizationDetails(patientId, authId);
      setSelectedAuth(freshAuth);
    } catch (err) {
      // Keep optimistic list row data if detail refresh fails.
      console.warn('Failed to load fresh authorization details for PA detail view:', err);
    }
  }, [patientId]);

  const closePADetail = useCallback(() => {
    setSelectedAuth(null);
    setPaDetailMode(null);
  }, []);

  const handlePASaved = useCallback(async (savedAuth?: Authorization) => {
    if (paDetailMode === 'create' && savedAuth) {
      // New PA created: stay in edit mode for follow-up updates
      setSelectedAuth(savedAuth);
      setPaDetailMode('edit');
      await refreshAuthorizations();
    } else if (savedAuth) {
      // Edit save: stay in detail view with updated auth
      setSelectedAuth(savedAuth);
      setPaDetailMode('edit');
      await refreshAuthorizations();
      // Refresh dashboard when authorization reaches a status that affects patient state
      if (savedAuth.status && DASHBOARD_REFRESH_STATUSES.has(savedAuth.status as PAStatus)) {
        refreshDashboard?.();
      }
    } else {
      // Submit/approve/deny/pend: close and refresh
      closePADetail();
      await refreshAuthorizations();
      refreshDashboard?.();
    }
  }, [paDetailMode, closePADetail, refreshAuthorizations, refreshDashboard]);

  // View handler — routes PA records to AuthorizationDetail, legacy to form
  const handleView = useCallback((id: string | number) => {
    const auth = authorizations.find((a) => a.id === id);
    if (!auth) return;
    if (AuthorizationsService.isPARecord(auth)) {
      openPADetail(auth, 'view');
    } else {
      form.openViewForm(auth);
    }
  }, [authorizations, openPADetail, form]);

  // Alert deep link (R-209): open the authorization with that id once the list has loaded
  const handledFocusRef = useRef<string | null>(null);
  useEffect(() => {
    if (!focusId) {
      handledFocusRef.current = null;
      return;
    }
    if (!patientId || !expanded || !hasLoadedOnce || handledFocusRef.current === focusId) return;
    handledFocusRef.current = focusId;
    const auth = authorizations.find((a) => a.authorizationId === focusId || String(a.id) === focusId);
    if (auth) {
      handleView(auth.id);
    } else {
      // Not in the list (e.g. filtered out): load it directly
      AuthorizationsService.getAuthorizationDetails(patientId, focusId)
        .then((fresh) => {
          setSelectedAuth(fresh);
          setPaDetailMode('view');
        })
        .catch(() => setError('The authorization from the alert could not be opened.'));
    }
    onFocusHandled?.();
  }, [focusId, patientId, expanded, hasLoadedOnce, authorizations, handleView, onFocusHandled, setError]);

  // Edit handler
  const handleEdit = useCallback((id: string | number) => {
    const auth = authorizations.find((a) => a.id === id);
    if (!auth) return;
    if (AuthorizationsService.isPARecord(auth)) {
      openPADetail(auth, 'edit');
    } else {
      form.openEditForm(auth);
    }
  }, [authorizations, openPADetail, form]);

  // Delete handler: confirm first, then soft-delete (only DRAFT authorizations can be deleted)
  const handleDelete = (id: string | number) => {
    if (!patientId) return;
    setDeleteTargetId(id);
  };

  const confirmDelete = async () => {
    if (!patientId || deleteTargetId === null) return;
    try {
      setDeleting(true);
      const auth = authorizations.find(a => a.id === deleteTargetId);
      await AuthorizationsService.deleteAuthorization(patientId, auth?.authorizationId || deleteTargetId);
      setDeleteTargetId(null);
      showToast('Authorization deleted.', { variant: 'success' });
      await refreshAuthorizations();
    } catch (err) {
      console.error('Error deleting authorization');
      setDeleteTargetId(null);
      showToast(describeApiError(err, 'Failed to delete authorization.'), { variant: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  // Copy handler
  const handleCopy = useCallback(async (id: string | number) => {
    if (!patientId) return;
    try {
      const authId = String(id);
      const auth = authorizations.find(a => a.id === id);
      const sourceId = auth?.authorizationId || authId;
      const copied = await AuthorizationsService.copyPA(patientId, sourceId);
      await refreshAuthorizations();
      openPADetail(copied, 'edit');
    } catch (err) {
      setError(describeApiError(err, 'Failed to copy authorization'));
    }
  }, [patientId, authorizations, refreshAuthorizations, openPADetail, setError]);

  // New PA handler
  const handleNewPA = useCallback(() => {
    setPaDetailMode('create');
    setSelectedAuth(null);
  }, []);

  // Check if showing PA detail or legacy form
  const showingPADetail = paDetailMode !== null;
  const showingLegacyForm = form.isEditing;
  const showList = !showingPADetail && !showingLegacyForm;

  // Alert banner for no patient selected
  const alertBanner = !patientId ? (
    <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
      <div className="flex items-start">
        <AlertCircle className="h-5 w-5 text-amber-600 mr-2 mt-0.5" />
        <p className="text-sm text-amber-700">
          Please select a patient to view and manage authorizations.
        </p>
      </div>
    </div>
  ) : undefined;

  return (
    <GenericListView
      title="Prior Authorizations"
      icon={<Key size={20} />}
      expanded={expanded}
      onToggleExpand={toggleExpanded}
      alertBanner={alertBanner}
      items={authorizations}
      keyExtractor={(auth) => String(auth.id)}
      columns={getAuthorizationColumns()}
      actions={getAuthorizationActions(handleView, handleEdit, handleDelete, handleCopy, canUpdateAuthorization, canDeleteAuthorization, isReadOnlyView)}
      mobileCard={getAuthorizationMobileCard(handleView, handleEdit, handleDelete, handleCopy, canUpdateAuthorization, canDeleteAuthorization, isReadOnlyView)}
      onRefresh={refreshAuthorizations}
      refreshing={refreshing}
      loading={loading}
      newButtonLabel="Add Authorization"
      onNewClick={handleNewPA}
      showNew={canCreateAuthorization}
      error={error}
      onDismissError={() => setError(null)}
      hasLoadedOnce={hasLoadedOnce}
      emptyStateIcon={
        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-slate-300">
          <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"></path>
          <polyline points="14 2 14 8 20 8"></polyline>
        </svg>
      }
      emptyStateTitle="No authorizations available"
      emptyStateMessage="Start by creating a new PA request using the button above."
      showList={showList}
    >
      {/* PA Detail View */}
      {showingPADetail && patientId && (
        <AuthorizationDetail
          authorization={selectedAuth}
          patientId={patientId}
          patientName={contextPatient?.firstName ? `${contextPatient.firstName} ${contextPatient.lastName || ''}` : undefined}
          mode={paDetailMode!}
          onBack={closePADetail}
          onSaved={handlePASaved}
          onError={(msg) => setError(msg)}
          isReadOnlyView={isReadOnlyView}
          canUpdateAuthorization={canUpdateAuthorization}
          canDeleteAuthorization={canDeleteAuthorization}
        />
      )}

      <ConfirmDialog
        open={deleteTargetId !== null}
        title="Delete authorization"
        message="Delete this draft authorization? It is removed from the list; its documents and audit history are kept."
        confirmLabel="Delete"
        destructive
        busy={deleting}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTargetId(null)}
      />

      {/* Legacy Form UI */}
      {showingLegacyForm && (
        <AuthorizationForm
          form={form}
          isReadOnlyView={isReadOnlyView}
          canUpdateAuthorization={canUpdateAuthorization}
          canDeleteAuthorization={canDeleteAuthorization}
        />
      )}

      {/* Form errors */}
      {form.errors.general && form.errors.general.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
          <ul className="list-disc list-inside text-sm text-red-700">
            {form.errors.general.map((err, i) => (
              <li key={i}>{err}</li>
            ))}
          </ul>
        </div>
      )}
    </GenericListView>
  );
};

export default Authorizations;
